import {MdFrameBlock, MdFrameItem, MdFrameStick} from "../../model/frame/MdFrameBlock";
import {FrmVar} from "../../drawfrm/FrmVar";
import {inches, mm} from "../../const";
import {MdFrameSpec} from "../../model/frame/MdFrameSpec";
import type {FrameGridView} from "./FrameGridScene";
import type {FrameBlockItem} from "./FrameBlockItem";
import type {StickTermination} from "./StickMaterial";

// format a dimension FrmVar as mm, or "" when there's no dimension to show. A pinned
// (user-set) size is marked "*".
function dimStr(v?: FrmVar): string {
    return v ? ` ${(v.get() / mm).toFixed(1)}mm${v.isModified() ? "*" : ""}` : "";
}

// "kind(id) size" label for a block node.
function blockLabel(block: MdFrameBlock): string {
    return block.kind + "(" + block.id + ")" + dimStr(block.size);
}

// build the nested, JSON-serializable view of the block tree: each block keyed by
// its "kind(id) size" label, children as an array of leaf strings (items/sticks)
// or nested block objects. Shared by the console dump and the StatusBar debug
// popover so both read the same structure.
export function frameBlocksDebugTree(block: MdFrameBlock): any {
    return {[blockLabel(block)]: blockChildren(block)};
}

function stickLabel(stick: MdFrameStick): string {
    return (stick.airGap ? "airgap(" : "stick(") + stick.id + ")" + dimStr(stick.thickness);
}

function itemLabel(it: MdFrameItem): string {
    const size = it.kind === "doors"
        ? ` ${(it.width.get() / mm).toFixed(1)}mm×${(it.height.get() / mm).toFixed(0)}mm`
        : dimStr(it.size);
    return it.kind + "(" + it.id + ")" + size;
}

function blockChildren(block: MdFrameBlock): any[] {
    return block.children.map(c => {
        if(c.kind === "item") return itemLabel(c.item);
        if(c.kind === "stick") return stickLabel(c.stick);
        return {[blockLabel(c.block)]: blockChildren(c.block)};
    });
}

// a snapshot of the frame grid for debugging, as JSON with lengths in inches: the
// model tree, where each cell and stick piece landed in the last layout (with each
// end's cut), the outline corners and steps, and the saved design (enough to rebuild
// the same frame). Without a frame grid on the canvas, the layout parts are empty.
export function frameLayoutDump(spec: MdFrameSpec, view: FrameGridView | null): string {
    const cells = view ? (view.grid.getItems() as FrameBlockItem[]).map(c => ({
        id: c.model.id,
        kind: c.model.kind,
        ...rectIn(c.layoutRect),
    })) : [];

    const sticks = view ? view.sticks.filter(s => !s.destroyed).map(s => ({
        id: s.id,
        stickId: s.stickId,
        visible: s.graphic.visible,
        airGap: s.airGap,
        orientation: s.orientation,
        x: toIn(s.positionX),
        y: toIn(s.positionY),
        length: toIn(s.length.get()),
        thickness: toIn(s.thickness.get()),
        endA: endStr(s.termination("A"), s.modelEnds.A),
        endB: endStr(s.termination("B"), s.modelEnds.B),
    })) : [];

    const steps = Object.fromEntries(Array.from(spec.stepInfo.entries())
        .map(([id, st]) => [id, {height: toIn(st.height), extension: toIn(st.extension)}]));

    return JSON.stringify({
        tree: frameBlocksDebugTree(spec.frameBlocks),
        cells,
        sticks,
        corners: spec.cornerInfo,
        steps,
        design: spec.toDesign(),
    }, null, 2);
}

function toIn(v: number): number {
    return Math.round(v / inches * 1000) / 1000;
}

function rectIn(r?: {x: number; y: number; width: number; height: number}) {
    if(!r) return {};
    return {x: toIn(r.x), y: toIn(r.y), width: toIn(r.width), height: toIn(r.height)};
}

// an end's cut, e.g. "miter(left, 2)" or "cap"; "~" marks an end that isn't the model
// stick's own end (a piece split off beside a step).
function endStr(t: StickTermination, modelEnd: boolean): string {
    const own = modelEnd ? "" : "~";
    if(t.kind === "cap") return own + "cap";
    if(t.kind === "miter") return `${own}miter(${t.direction}, ${toIn(t.otherThickness.get())})`;
    return `${own}extend(${toIn(t.otherThickness.get())})`;
}
